import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Panel } from "@/components/admin/shared";
import { PhotoCard } from "@/components/admin/gallery/PhotoCard";
import { CategoryManager } from "@/components/admin/gallery/CategoryManager";
import { BeforeAfterManager } from "@/components/admin/gallery/BeforeAfterManager";
import { CategorySelect } from "@/components/admin/gallery/CategorySelect";

const Uploader = ({ categories, onDone }) => {
  const [cat, setCat] = useState("");
  const [progress, setProgress] = useState(null);
  const upload = async (files) => {
    const list = [...files];
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      setProgress(`${i + 1} de ${list.length}`);
      const fd = new FormData();
      fd.append("file", list[i]);
      fd.append("category_id", cat);
      try { await api.post("/admin/gallery/photos", fd); ok++; } catch (e) { toast.error(`${list[i].name}: ${errorText(e)}`); }
    }
    setProgress(null);
    if (ok) toast.success(`${ok} foto(s) subidas como borrador. Agrega título y texto ALT y presiona «Publicar».`);
    onDone();
  };
  return (
    <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
      <label className="block text-xs uppercase tracking-wider text-mute">Categoría para las fotos nuevas
        <CategorySelect categories={categories} value={cat} onChange={setCat} id="upload-category" />
      </label>
      <label className={`btn-gold cursor-pointer ${progress ? "pointer-events-none opacity-70" : ""}`} data-testid="photo-upload-btn">
        {progress ? <><Loader2 size={16} className="animate-spin" /> Subiendo {progress}</> : <><Upload size={16} /> Subir fotos</>}
        <input type="file" accept="image/*" multiple className="sr-only" onChange={(e) => { if (e.target.files.length) upload(e.target.files); e.target.value = ""; }} data-testid="photo-upload-input" />
      </label>
    </div>
  );
};

export const GalleryTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/gallery").then((r) => setData(r.data)).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loader2 className="animate-spin text-brand" />;
  const move = async (i, dir) => {
    const ids = data.photos.map((p) => p.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await api.post("/admin/gallery/photos/reorder", { ids }); } catch (e) { toast.error(errorText(e)); }
    load();
  };
  return (
    <div className="space-y-6" data-testid="gallery-admin">
      <Panel title="Administrar galería fotográfica">
        <p className="mb-4 text-sm text-mute">Puedes elegir varias fotos a la vez, desde tu computadora o tu celular. Se comprimen solas y quedan como <b>borrador</b>. Al presionar <b>Publicar</b> aparecen al instante en /galeria, sin volver a publicar el sitio.</p>
        <Uploader categories={data.categories} onDone={load} />
      </Panel>
      <Panel title={`Fotos (${data.photos.length})`}>
        {!data.photos.length && <p className="text-sm text-mute">Aún no hay fotos.</p>}
        <div className="grid gap-4 lg:grid-cols-2">
          {data.photos.map((p, i) => <PhotoCard key={p.id} p={p} categories={data.categories} onChange={load} onMove={(d) => move(i, d)} first={i === 0} last={i === data.photos.length - 1} />)}
        </div>
      </Panel>
      <BeforeAfterManager photos={data.photos} pairs={data.before_after} onChange={load} />
      <CategoryManager categories={data.categories} onChange={load} />
    </div>
  );
};
